import { useEffect, useMemo, useState } from "react";
import api, { BACKEND_URL } from "@/lib/api";
import { useSignedFiles } from "@/hooks/useSignedFiles";
import { FullProfile } from "@/components/FullProfile";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { X, Loader2, Download, FileText, Share2, Target } from "lucide-react";

const tokenize = (s) => (s || "").toLowerCase().split(/[^a-z0-9+#.]+/).filter(Boolean);

// Recruiter-facing full CV profile of a candidate, with role-match highlights + share.
export function EmployerProfileDrawer({ candidateId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(false);
  const [sharing, setSharing] = useState(false);
  useEffect(() => {
    api.get(`/candidates/${candidateId}/profile`).then((r) => setData(r.data)).catch(() => setErr(true)).finally(() => setLoading(false));
  }, [candidateId]);
  const token = localStorage.getItem("hs_token");
  const p = data?.profile;
  const cvPath = p?.cv_url ? p.cv_url.replace(/^.*\/files\//, "") : null;
  const signUrl = useSignedFiles(cvPath ? [cvPath] : []);

  const { matched, jobTitle } = useMemo(() => {
    const jobTerms = (data?.job?.terms || []).map((t) => (t || "").toLowerCase()).filter(Boolean);
    const isMatch = (term) => {
      const t = (term || "").toLowerCase();
      if (!t) return false;
      if (t.length <= 3) return jobTerms.some((jt) => jt === t || tokenize(jt).includes(t));
      return jobTerms.some((jt) => jt === t || jt.includes(t) || t.includes(jt) || tokenize(jt).includes(t));
    };
    const m = new Set();
    [...(p?.skills || []), ...(p?.tools || [])].forEach((s) => {
      const n = typeof s === "string" ? s : s?.name;
      if (n && jobTerms.length && isMatch(n)) m.add(n.toLowerCase());
    });
    return { matched: m, jobTitle: data?.job?.title };
  }, [data, p]);

  const share = async () => {
    setSharing(true);
    try {
      const { data: res } = await api.post(`/candidates/${candidateId}/share`);
      const url = `${window.location.origin}${res.path}`;
      try { await navigator.clipboard.writeText(url); toast.success("Read-only profile link copied — valid for 30 days"); }
      catch { toast.message("Share link ready", { description: url }); }
    } catch {
      toast.error("Could not create a share link");
    } finally { setSharing(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="employer-profile-drawer">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-2xl h-full overflow-y-auto bg-card dark:bg-[#0d0f14] border-l border-border p-6">
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Candidate CV profile</div>
            <h2 className="text-2xl font-display font-semibold">{data?.candidate?.name || "Profile"}</h2>
            {p?.headline && <p className="text-sm text-muted-foreground">{p.headline}</p>}
          </div>
          <button onClick={onClose} data-testid="close-employer-profile" className="text-muted-foreground hover:text-foreground"><X className="h-5 w-5" /></button>
        </div>
        {loading ? (
          <div className="flex items-center gap-2 text-muted-foreground py-12 justify-center"><Loader2 className="h-5 w-5 animate-spin" /> Loading profile…</div>
        ) : err ? (
          <div className="text-center py-12 text-muted-foreground" data-testid="employer-profile-error"><FileText className="h-10 w-10 mx-auto mb-3 opacity-50" /><p>Unable to load this profile. Please close and try again.</p></div>
        ) : !p || p.status === "empty" ? (
          <div className="text-center py-12 text-muted-foreground"><FileText className="h-10 w-10 mx-auto mb-3 opacity-50" /><p>This candidate hasn't confirmed a full CV profile yet.</p></div>
        ) : (
          <>
            <div className="flex flex-wrap gap-2 mb-4">
              {p.cv_url && (
                <a href={signUrl(cvPath)} target="_blank" rel="noreferrer" data-testid="employer-download-cv">
                  <Button variant="outline" size="sm" className="gap-1.5"><Download className="h-4 w-4" /> Original CV</Button>
                </a>
              )}
              <Button variant="outline" size="sm" onClick={share} disabled={sharing} data-testid="employer-share-profile" className="gap-1.5">
                {sharing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Share2 className="h-4 w-4" />} Share with panel
              </Button>
            </div>

            {matched.size > 0 && (
              <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-3 mb-5 flex items-center gap-2.5" data-testid="match-banner">
                <Target className="h-4 w-4 text-emerald-400 shrink-0" />
                <p className="text-sm text-emerald-200">
                  <strong>{matched.size}</strong> CV term{matched.size === 1 ? "" : "s"} directly match{matched.size === 1 ? "es" : ""} {jobTitle ? `${jobTitle}'s` : "this role's"} requirements — highlighted below.
                </p>
              </div>
            )}

            <FullProfile profile={p} matched={matched} />
          </>
        )}
      </div>
    </div>
  );
}
